/**
 * GAP-2 — C Mode framing for Plan-of-Record change requests (SPEC-00 §4.1).
 *
 * Shown when a pending proposal would supersede an existing ratified/published
 * cycle_report. Reuses Brief G versioning; no new schema.
 */

import { Link } from 'react-router-dom';
import { ArrowRightLeft, FileText } from 'lucide-react';
import type { AmendmentLogEntry } from '../../../stores/prioritization/cycleReportStore';
import {
  amendmentTypeLabel,
  type PorChangeRequestContext,
} from '../../../lib/porChangeRequest';

interface PorChangeRequestBannerProps {
  cycleId: string;
  context: PorChangeRequestContext;
  /** Optional client-side preview of amendments if accept proceeds */
  previewDiff?: AmendmentLogEntry[];
  className?: string;
}

export function PorChangeRequestBanner({
  cycleId,
  context,
  previewDiff = [],
  className = '',
}: PorChangeRequestBannerProps) {
  if (!context.isChangeRequest || context.currentVersion == null) return null;

  const preview = previewDiff.slice(0, 6);
  const more = Math.max(0, previewDiff.length - preview.length);

  return (
    <section
      className={` border border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)]/80 dark:bg-[var(--slate)]/30 px-4 py-3.5 ${className}`}
      aria-label="Cycle report change request"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center bg-[var(--slate-dim)] dark:bg-[var(--slate)]/50">
          <ArrowRightLeft className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Change request — would become Priorities v{context.nextVersion}
          </p>
          <p className="mt-1 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
            This cycle already has a{' '}
            <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {context.legitimacyState}
            </span>{' '}
            cycle report (v{context.currentVersion}). Accepting this proposal writes a new
            version and supersedes the current one.
          </p>

          {preview.length > 0 && (
            <div className="mt-3 border border-[var(--slate)]/80 dark:border-[var(--slate)]/40 bg-[var(--bg-raised)]/90 dark:bg-[var(--bg-raised)] px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
                Preview of changes
              </p>
              <ul className="space-y-1">
                {preview.map((entry) => (
                  <li
                    key={`${entry.type}-${entry.item_id}-${entry.new_rank ?? entry.previous_rank}`}
                    className="flex items-center gap-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]"
                  >
                    <span
                      className={`rounded px-1.5 py-0.5 font-medium ${
                        entry.type === 'added'
                          ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                          : entry.type === 'removed'
                            ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
                            : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                      }`}
                    >
                      {amendmentTypeLabel(entry.type)}
                    </span>
                    <span className="truncate font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                      {entry.item_title}
                    </span>
                    {entry.type === 'rank_change' && (
                      <span className="text-[var(--text-ter)] flex-shrink-0">
                        #{entry.previous_rank} → #{entry.new_rank}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {more > 0 && (
                <p className="mt-1.5 text-xs text-[var(--text-ter)]">+{more} more after accept</p>
              )}
            </div>
          )}

          <div className="mt-3">
            <Link
              to={`/prioritization/cycles/${cycleId}/report`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
            >
              <FileText className="h-3.5 w-3.5" />
              View current cycle report (v{context.currentVersion})
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Compact badge for inbox list cards */
export function PorChangeRequestBadge({
  currentVersion,
}: {
  currentVersion: number;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]">
      <ArrowRightLeft className="h-3 w-3" />
      Change request · v{currentVersion} → v{currentVersion + 1}
    </span>
  );
}
